import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import {
  getContractErrors,
  getNonExpiredContracts,
  getSendMasterDataChangeRequestResponse,
  ResetMasterDataChange,
  WhitelabelImage,
  WhitelabelImageAltTag,
} from '@resident-nx/shared';
import { WhitelabelImageWebComponent } from '@resident-nx/web/ui';
import { SideSheetContentDirective } from '../../../organisms/side-sheet-form/directives/side-sheet-content.directive';
import { SideSheetFormComponent } from '../../../organisms/side-sheet-form/side-sheet-form.component';

@Component({
  standalone: true,
  selector: 'rs-web-payment-change-request-response',
  templateUrl: './payment-change-request-response.component.html',
  styleUrl: './payment-change-request-response.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    SideSheetContentDirective,
    SideSheetFormComponent,
    TranslateModule,
    NgTemplateOutlet,
    WhitelabelImageWebComponent,
  ],
})
export class PaymentChangeRequestResponseComponent implements OnDestroy {
  #store = inject(Store);
  readonly response = this.#store.selectSignal(getSendMasterDataChangeRequestResponse);
  readonly contractErrors = toSignal(
    this.#store
      .select(getNonExpiredContracts)
      .pipe(map(contracts => getContractErrors(this.response(), contracts)))
  );
  readonly successAmount = computed(() => {
    // Null when the mutation failed outright — no statuses, nothing succeeded.
    const statuses = this.response()?.changeRequestStatuses ?? [];

    return statuses.filter(status => status.success).length;
  });
  readonly headerText = computed(() => {
    switch (this.successAmount()) {
      case this.response()?.changeRequestStatuses?.length:
        return 'user_payment_edit.form_h';
      case 0:
        return 'user_edit_error_heading';
      default:
        return 'user_edit_half_error_heading';
    }
  });

  ngOnDestroy(): void {
    this.#store.dispatch(ResetMasterDataChange());
  }

  protected readonly WhitelabelImageAltTag = WhitelabelImageAltTag;
  protected readonly whitelabelImage = WhitelabelImage;
}
